<template>
  <div class="page product">
    <PagesProductDetails :product="product" />
    <PagesProductProductSwiper
      :items="product.proposte"
      :title="structuredTitle"
    />
    <PagesProductProductSwiper
      :items="filteredRelatedProducts"
      :title="title"
    />
  </div>
</template>

<script setup>
import { useStore } from '@/store/index.js'
import queries from '@/graphql/craft/queries/index.js'
import { useHelpers } from '~/composables/helpers.js'

// Fetch Data
const query = queries.product.getProduct
const route = useRoute()

const { configuration } = useStore()

const variables = computed(() => ({
  // Passiamo statico il site per via di algolia
  // che non gestisce il multilingua
  // La scheda prodotto su craft ha campi per ogni lingua, non usa sites
  // site: 'it',
  // Oppure usare entry(slug: $slug) e applicare il site dove serve la traduzione di Craft es. modalitaDuso(site: $site)
  site: configuration.site,
  slug: route.params.slug,
}))

const { t, locale } = useI18n()

const getLocalizedField = (field) => {
  switch (locale.value) {
    case 'en':
      return product.value[`${field}En`]
    case 'es':
      return product.value[`${field}Es`]
    case 'fr':
      return product.value[`${field}Fr`]
    default:
      return product.value[field]
  }
}

const title = computed(() => {
  return `<h2>${t('product.altriProdotti')}</h2>`
})

const structuredTitle = computed(() => {
  const staticText = t('product.structuredTitle', {
    title: product.value.title,
  })
  return staticText
})

const { data } = await useGraphql(query, variables)

if (!data.value?.entry) {
  throw createError({ statusCode: 404 })
}
const product = computed(() => data.value.entry)
const relatedProducts = computed(() => data.value.entries || [])

const filteredRelatedProducts = computed(() => {
  // Current product type
  const currentProductType = product.value.typeHandle

  // Categorie prodotto attuale
  const currentProductCategories = [
    ...(product.value.filtroCategoriaLievitoBirra || []),
    ...(product.value.filtroCategoriaProdottoVino || []),
  ].map((category) => category.key)

  // Filtra i prodotti correlati che hanno almeno una categoria in comune
  // con il prodotto attuale e sono dello stesso tipo (vino o birra)
  const relatedProductsFiltered = relatedProducts.value.filter(
    (relatedProduct) => {
      // Esclude il prodotto attuale dalla lista dei prodotti correlati
      if (relatedProduct.slug === product.value.slug) {
        return false
      }

      // Solo prodotti dello stesso tipo
      if (relatedProduct.typeHandle !== currentProductType) {
        return false
      }

      const relatedProductCategories = [
        ...(relatedProduct.filtroCategoriaLievitoBirra || []),
        ...(relatedProduct.filtroCategoriaProdottoVino || []),
      ].map((category) => category.key)

      // Controlla se almeno una categoria del prodotto correlato è presente
      // tra le categorie del prodotto attuale
      return relatedProductCategories.some((category) =>
        currentProductCategories.includes(category),
      )
    },
  )

  // Limita i prodotti correlati a un massimo di 9
  return relatedProductsFiltered.slice(0, 9)
})

const seo = computed(() => data.value?.entry?.seo || {})
const localeParams = computed(() => data.value?.entry?.localized || [])

const stripHtmlTags = (str) => {
  if (!str) return ''
  const withoutTags = str.replace(/<\/?[^>]+(>|$)/g, '')
  const firstSentence = withoutTags.split('.')[0]
  return firstSentence + '.'
}

// Seo
useSeo({
  ...seo.value,
  localeParams: useHelpers().generateSeoLocaleParams(localeParams, 'slug'),
  meta: [
    {
      name: 'description',
      content: stripHtmlTags(getLocalizedField('descrizioneProdotto')),
    },
  ],
})
</script>
